/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, ReactNode, useEffect, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import BrandedSpinner from "@/components/BrandedSpinner";

// ── Base path context ────────────────────────────────────────
// "/"                 → franchise subdomain (franchise.villarealgroup.com)
// "/franchise-portal" → main app / mobile (Option B role detection)
const FranchiseBasePathContext = createContext("/");
export const useFranchiseBasePath = () => useContext(FranchiseBasePathContext);
export function FranchiseBasePathProvider({
  basePath,
  children,
}: {
  basePath: string;
  children: ReactNode;
}) {
  return (
    <FranchiseBasePathContext.Provider value={basePath}>
      {children}
    </FranchiseBasePathContext.Provider>
  );
}

// ── Franchise user types ─────────────────────────────────────
type FranchiseBrand = {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  status: string;
  billing_model: string;
  primary_contact_email: string | null;
};

type FranchiseUserRecord = {
  id: string;
  franchise_brand_id: string;
  user_id: string;
  role: "franchise_admin" | "franchise_viewer";
  franchise_brands: FranchiseBrand;
};

type FranchiseUserContextType = {
  franchiseUser: FranchiseUserRecord;
  brand: FranchiseBrand;
  franchiseBrandId: string;
  role: "franchise_admin" | "franchise_viewer";
  isAdmin: boolean;
};

// ── Franchise user context ───────────────────────────────────
const FranchiseUserContext = createContext<FranchiseUserContextType | null>(null);

export function useFranchiseUser() {
  const ctx = useContext(FranchiseUserContext);
  if (!ctx) throw new Error("useFranchiseUser must be used inside RequireFranchiseUser");
  return ctx;
}

// ── Data fetching hook ───────────────────────────────────────
export function useFranchiseUserQuery() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["franchise-user", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("franchise_users")
        .select("*, franchise_brands(*)")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data as FranchiseUserRecord | null;
    },
    enabled: !!user,
    staleTime: 5 * 60_000,
  });
}

// ── Auth guard ───────────────────────────────────────────────
export function RequireFranchiseUser({ children }: { children: ReactNode }) {
  const { loading: authLoading, session, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: franchiseUser, isLoading } = useFranchiseUserQuery();

  useEffect(() => {
    if (!authLoading && !session) {
      const from = location.pathname + location.search;
      const target = from === "/" || from === "/auth"
        ? "/auth"
        : `/auth?redirect=${encodeURIComponent(from)}`;
      navigate(target, { replace: true });
    }
  }, [authLoading, session, navigate, location]);

  if (authLoading || isLoading) {
    return <BrandedSpinner size="full" />;
  }

  if (!session) return null;

  if (!franchiseUser) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#080816]">
        <div className="text-center space-y-4 px-4">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-violet-500/10 ring-1 ring-violet-400/20 mx-auto">
            <svg className="h-7 w-7 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <div>
            <p className="text-white font-semibold text-lg">No Franchise Access</p>
            <p className="text-gray-400 text-sm mt-1">
              This account doesn't have franchise portal access.<br />
              Contact your administrator for an invite link.
            </p>
          </div>
          <button
            onClick={() => signOut()}
            className="text-sm text-violet-400 hover:text-violet-300 transition-colors underline underline-offset-2"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const brand = franchiseUser.franchise_brands;
  const value: FranchiseUserContextType = {
    franchiseUser,
    brand,
    franchiseBrandId: franchiseUser.franchise_brand_id,
    role: franchiseUser.role,
    isAdmin: franchiseUser.role === "franchise_admin",
  };

  return (
    <FranchiseUserContext.Provider value={value}>
      {children}
    </FranchiseUserContext.Provider>
  );
}

// ── Franchise subscription gate ──────────────────────────────
// Must be used inside RequireFranchiseUser (needs franchiseBrandId from context)

type FranchiseSubState = {
  subscribed: boolean;
  tier: string | null;
  status: string | null;
  loading: boolean;
  checkFailed: boolean;
};

// ── Franchise subscription localStorage cache ─────────────────
// Same strategy as operator subscription: cache for 24h, never block mid-session on network failure.

const FRANCHISE_SUB_CACHE_TTL = 24 * 60 * 60 * 1000;

type FranchiseSubCache = {
  state: Omit<FranchiseSubState, "loading" | "checkFailed">;
  checkedAt: number;
};

function franchiseSubCacheKey(brandId: string) {
  return `vrg_franchise_sub_${brandId}`;
}

function readFranchiseSubCache(brandId: string): FranchiseSubCache | null {
  try {
    const raw = localStorage.getItem(franchiseSubCacheKey(brandId));
    if (!raw) return null;
    const parsed: FranchiseSubCache = JSON.parse(raw);
    if (Date.now() - parsed.checkedAt > FRANCHISE_SUB_CACHE_TTL) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeFranchiseSubCache(brandId: string, state: FranchiseSubState) {
  try {
    const entry: FranchiseSubCache = {
      state: { subscribed: state.subscribed, tier: state.tier, status: state.status },
      checkedAt: Date.now(),
    };
    localStorage.setItem(franchiseSubCacheKey(brandId), JSON.stringify(entry));
  } catch { /* localStorage unavailable */ }
}

export function RequireFranchiseSubscription({ children }: { children: ReactNode }) {
  const { franchiseBrandId, role } = useFranchiseUser();
  const basePath = useFranchiseBasePath();
  const [retrying, setRetrying] = useState(false);

  const cached = readFranchiseSubCache(franchiseBrandId);
  const [sub, setSub] = useState<FranchiseSubState>(() =>
    cached
      ? { ...cached.state, loading: false, checkFailed: false }
      : { subscribed: false, tier: null, status: null, loading: true, checkFailed: false }
  );

  const check = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke("check-franchise-subscription", {
        body: { franchise_brand_id: franchiseBrandId },
      });
      const result = data ?? {};
      if (error && !result.subscribed) {
        console.error("[FranchiseSub] Check failed:", error);
        throw error;
      }
      const newState: FranchiseSubState = {
        subscribed: result.subscribed ?? false,
        tier: result.tier ?? null,
        status: result.status ?? null,
        loading: false,
        checkFailed: false,
      };
      writeFranchiseSubCache(franchiseBrandId, newState);
      setSub(newState);
    } catch (e) {
      console.error("[FranchiseSub] Check error (background):", e);
      // Keep existing state silently — only mark checkFailed if we had no cached state
      setSub((prev) => {
        if (!prev.loading && !prev.checkFailed) return prev;
        return { ...prev, loading: false, checkFailed: true };
      });
    }
  }, [franchiseBrandId]);

  useEffect(() => { check(); }, [franchiseBrandId, check]);

  if (sub.loading) return <>{children}</>;

  // checkFailed with no cache — show non-blocking banner, keep working
  if (sub.checkFailed) {
    return (
      <>
        <div className="sticky top-0 z-50 flex items-center justify-between gap-3 px-4 py-2.5 text-sm bg-amber-500/10 border-b border-amber-400/20 text-amber-300">
          <span>Having trouble verifying your subscription — you can keep working while we retry.</span>
          <button
            disabled={retrying}
            onClick={async () => { setRetrying(true); await check(); setRetrying(false); }}
            className="shrink-0 rounded-md bg-amber-400/20 px-3 py-1 text-xs font-medium hover:bg-amber-400/30 transition-colors disabled:opacity-60"
          >
            {retrying ? "Retrying…" : "Retry"}
          </button>
        </div>
        {children}
      </>
    );
  }

  // Admins are never hard-blocked — show a sticky banner instead
  if (!sub.subscribed && role === "franchise_admin") {
    return (
      <>
        <div className="sticky top-0 z-50 flex items-center justify-between gap-3 px-4 py-2.5 text-sm bg-amber-500/10 border-b border-amber-400/20 text-amber-300">
          <span>No active subscription — some features may be limited.</span>
          <a href={basePath + "/billing"} className="underline text-amber-200 hover:text-white shrink-0">Subscribe →</a>
        </div>
        {children}
      </>
    );
  }

  if (!sub.subscribed) {
    const tiers = [
      { name: "Starter", stores: "1–20 stores", price: "$199/mo" },
      { name: "Growth", stores: "21–50 stores", price: "$399/mo" },
      { name: "Scale", stores: "51–150 stores", price: "$699/mo" },
    ];
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-lg w-full rounded-2xl border border-white/10 bg-[#0e0e23] p-8 text-center space-y-6">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-violet-500/10 ring-1 ring-violet-400/20 mx-auto">
            <svg className="h-7 w-7 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <p className="text-white font-bold text-xl">Franchise Portal Subscription Required</p>
            <p className="text-gray-400 text-sm mt-2">
              Access the full analytics suite for your franchise network. Choose a plan below.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-left">
            {tiers.map((t) => (
              <div key={t.name} className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-1">
                <p className="text-white font-semibold text-sm">{t.name}</p>
                <p className="text-xs text-gray-400">{t.stores}</p>
                <p className="text-violet-300 font-bold">{t.price}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500">
            Contact <a href="mailto:billing@villarealgroup.com" className="text-violet-400 hover:underline">billing@villarealgroup.com</a> to activate your subscription.
          </p>
          <a href="/" className="inline-block text-sm text-violet-400 hover:text-violet-300 underline underline-offset-2 mt-2">
            Go to Home
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
